import React from 'react';
import { NumberInputDisplay } from '../../Shared/Scripts/Components/ValgDisplayFunctions';
import VeilederStegComponent, { VeilederStegBaseProps } from '../../Shared/Scripts/Components/VeilederStegComponent';
import { EpiAppStateMedSteg } from '../../Shared/Scripts/Store/Types';
import { connect } from 'react-redux';
import { setCollapsed } from '../../Shared/Scripts/Store/StegVisning/Actions';
import contentResolver from '../../Shared/Scripts/Utilities/EpiContentResolver';
import { EpiContext } from '../../Shared/Scripts/Store/EpiContext/Types';
import { ValideringsResultat } from '../../Shared/Scripts/Models/ValideringsResultat';
import { NumberValidering, validerMinMax } from '../../Shared/Scripts/Utilities/Validering';
import { ArstimerProsentValg } from '../Models/ArstimerProsentValg';
import { LegendWithErrorComponent } from '../../Shared/Scripts/Components/LegendWithErrorComponent';

export interface NumberFormatter {
    // Konverterer tall til en streng formattert for visning
    toFormattetString: (tall: number) => string;
    // Konverterer en streng som er formattert for visning til en streng som kan parses til tall
    toParsableAsNumber: (verdi: string) => string;
}

export interface NumberInputType<T> {
    inputTypeId: string;
    inputTypeLabel: string;
    placeholder: string;
    validering: NumberValidering<T>;
    preValidation?: ValideringsResultat;
}

type NumberInputOwnProps<T> = VeilederStegBaseProps & {
    inputId: string;
    sporsmal: keyof T;
    value?: number | string;
    vetIkke: boolean;
    vetIkkeLabel: string;
    selectLabel: string;
    isSmall?: boolean;
    decimals: number;
    ctaText: keyof T;
    inputTypes: Array<NumberInputType<T>>;
    onInputChange: (valg?: ArstimerProsentValg) => void;
    onValidationChanged: (result: ValideringsResultat | undefined) => void;
};

type NumberInputProps<T> = NumberInputOwnProps<T> & MapStateToProps<T> & MapDispatchToProps;

interface NumberInputState<T> {
    verdi: number | string;
    currentInputTypeId: string;
    vetIkke: boolean;
    valideringsResultat?: ValideringsResultat;
    isInErrorState: boolean;
    isValidValue: boolean
}

class DArstimerProsentComponent<T> extends React.PureComponent<NumberInputProps<T>, NumberInputState<T>> {
    constructor(props: NumberInputProps<T>) {
        super(props);
        this.state = {
            verdi: props.value !== undefined ? props.value : '',
            currentInputTypeId: props.inputTypes[0].inputTypeId,
            valideringsResultat: props.inputTypes[0].preValidation,
            vetIkke: props.vetIkke,
            isInErrorState: false,
            isValidValue: false
        };

        this.onInputChange = this.onInputChange.bind(this);
        this.confirmValue = this.confirmValue.bind(this);
        this.onSelectedValueChange = this.onSelectedValueChange.bind(this);
    }

    onInputKeyUp(e: KeyboardEvent): void {
        if (e.key === 'enter') {
            this.confirmValue();
        }
    }

    confirmValue(): void {
        const {
            onInputChange,
            stegVisningSetCollapsed,
            sporsmalId,
            epiContext,
            onValidationChanged,
            inputTypes,
        } = this.props;
        const { currentInputTypeId, vetIkke, verdi } = this.state;
        const currentInputType = inputTypes.find((v) => v.inputTypeId === currentInputTypeId) as NumberInputType<T>;
        const resultat = this.valider(verdi, epiContext, currentInputType.validering);
        onValidationChanged(resultat);
        onInputChange({
            type: currentInputTypeId,
            verdi: verdi,
            vetIkke: vetIkke,
        });
        if ((resultat && resultat.isValid) || vetIkke) {
            this.visErrorMessage(false);
            stegVisningSetCollapsed({ id: sporsmalId, isCollapsed: true });

            this.setState({
                valideringsResultat: resultat,
                isValidValue: true,
            });
        } else {
            this.visErrorMessage(true);
            this.setState({
                valideringsResultat: resultat,
                isValidValue: false,
            });
        }
    }

    onSelectedValueChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        this.visErrorMessage(false);
        this.setState({
            verdi: '',
            valideringsResultat: undefined,
            currentInputTypeId: e.target.value,
        });
    }

    onInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            verdi: e.target.value,
        });
    }

    valider(
        verdi: number | string,
        epiContext: EpiContext<T>,
        validering: NumberValidering<T>,
    ): ValideringsResultat | undefined {
        return validerMinMax(epiContext, verdi, validering);
    }

    visErrorMessage(show: boolean): void {
        document.getElementById(this.props.inputId)?.focus();
        this.setState((previousState) => ({ ...previousState, isInErrorState: show }));
    }

    displayElement(
        parsedVerdi: number | string,
        sporsmalId: string,
        postfix: string,
        vetIkke: boolean,
        vetIkkeLabel: string,
    ): JSX.Element {
        if (vetIkke) {
            return (
                <div className="c-veileder__valgt-item">
                    <span className="c-veileder__tekst c-veileder__tekst--valg">{vetIkkeLabel}</span>
                </div>
            );
        }

        return parsedVerdi !== null ? (
            NumberInputDisplay(sporsmalId, {
                id: '',
                displayValue: `${parsedVerdi} ${postfix}`,
            })
        ) : (
            <div />
        );
    }

    render(): JSX.Element {
        const {
            inputId,
            sporsmalId,
            tittel,
            forklaring,
            sporsmal,
            epiContext,
            ctaText,
            inputTypes,
            vetIkkeLabel,
            selectLabel,
        } = this.props;

        const {
            currentInputTypeId,
            valideringsResultat: currentResultat,
            vetIkke,
            isInErrorState
        } = this.state;

        const currentInputType = inputTypes.find((v) => v.inputTypeId === currentInputTypeId) as NumberInputType<T>;

        const selectOptions = inputTypes.map((v) => (
            <option
                className="c-dropdown-input__dropdown-option"
                key={v.inputTypeId}
                id={v.inputTypeId}
                value={v.inputTypeId}
            >
                {v.inputTypeLabel}
            </option>
        ));

        const selectId = sporsmalId + '-select';
        const vetIkkeInputId = sporsmalId + '-vetIkkeInput';
        return (
            <VeilederStegComponent
                sporsmalId={sporsmalId}
                tittel={tittel}
                forklaring={forklaring}
                inputArea={
                    <div>
                        <div
                            className={`c-veileder__inputomrade ${
                                !isInErrorState ? '' : 'c-veileder__inputomrade--feil'
                            }`}
                        >
                            <fieldset className="c-veileder__fieldset">
                                <LegendWithErrorComponent
                                    feilMeldingsId="oppgi-skolepenger-input"
                                    errorElement={currentResultat?.feilElement}
                                    showError={!!(isInErrorState && currentResultat)}
                                >
                                    <h3 className="c-veileder__undersporsmal">
                                        {contentResolver(epiContext, sporsmal)}
                                    </h3>
                                </LegendWithErrorComponent>
                                <div
                                    className={['c-number-input', isInErrorState ? 'c-number-input--feil' : ''].join(
                                        ' ',
                                    )}
                                >
                                    <input
                                        className="c-number-input__input c-number-input__input-with-dropdown"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        type="text"
                                        aria-describedby="oppgi-skolepenger-input"
                                        placeholder={currentInputType.placeholder}
                                        aria-invalid={isInErrorState}
                                        id={inputId}
                                        onKeyUp={(e: React.KeyboardEvent): void => {
                                            if (e.key === 'Enter') {
                                                this.confirmValue();
                                            }
                                        }}
                                        onChange={this.onInputChange}
                                        value={this.state.verdi}
                                        disabled={vetIkke}
                                    />
                                    <div className="c-number-input__dropdown-wrapper">
                                        <select
                                            id={selectId}
                                            value={currentInputTypeId}
                                            className="c-number-input__dropdown"
                                            onChange={this.onSelectedValueChange}
                                            disabled={vetIkke}
                                        >
                                            {selectOptions}
                                        </select>
                                        <div className="c-number-input__dropdown-arrow" aria-hidden={true}></div>
                                        <label className="sr-only" htmlFor={selectId}>
                                            {selectLabel}
                                        </label>
                                    </div>
                                </div>
                                <div className="c-veileder__checkbox">
                                    <input
                                        id={vetIkkeInputId}
                                        type="checkbox"
                                        value="vetIkke"
                                        checked={vetIkke}
                                        onChange={(v): void => {
                                            this.setState({ vetIkke: v.target.checked });
                                        }}
                                    ></input>
                                    <label htmlFor={vetIkkeInputId}>{vetIkkeLabel}</label>
                                </div>
                            </fieldset>
                        </div>
                        <div className="c-veileder__cta">
                            <button onClick={this.confirmValue} className="c-knapp c-knapp--cta c-knapp--secondary">
                                {contentResolver(epiContext, ctaText)}
                            </button>
                        </div>
                    </div>
                }
                displayElement={this.displayElement(
                    this.state.verdi,
                    sporsmalId,
                    currentInputType.inputTypeLabel,
                    vetIkke,
                    vetIkkeLabel,
                )}
                canCollapse={this.state.isValidValue && (!isInErrorState || vetIkke)}
                neverCollapse={false}
            />
        );
    }
}

interface MapStateToProps<T> {
    epiContext: EpiContext<T>;
}

interface MapDispatchToProps {
    stegVisningSetCollapsed: typeof setCollapsed;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function DArstimerProsentContainer<T>() {
    return connect(
        (state: EpiAppStateMedSteg<T>): MapStateToProps<T> => {
            return {
                epiContext: state.epiContext,
            };
        },
        { stegVisningSetCollapsed: setCollapsed },
    )(DArstimerProsentComponent as new (props: NumberInputProps<T>) => DArstimerProsentComponent<T>);
}
